// External
import { useCallback, useRef } from 'react';

// Internal
import {
  addressAreEqual,
  buildAmountBreakdown,
  normalizeAddressForMerchant,
  normalizeShippingOption,
} from '@/lib/utils/shipping';
import type {
  Breakdown,
  EmptyObject,
  OnShippingChange,
  OnShippingChangeCallbackWrapper,
  OnShippingChangeCallbackWrapperPayload,
} from '@/lib/utils/shipping/types';
import { noop } from '@/lib/utils/miscUtils';
import { logger } from '@/lib/logger';

/**
 * @NOTE
 * `useShippingChangeCallback` is a hook that wraps the `onShippingChange`
 * callback from the sdk. It can return:
 *   true  - the callback has run successfully
 *   false - the callback was skipped
 */
export const useShippingChangeCallback = (
  cartId: string,
  amounts: Breakdown,
): OnShippingChangeCallbackWrapper => {
  const onShippingChangeCb = window?.xprops?.onShippingChange as
    | OnShippingChange
    | undefined;
  const merchantShippingAddressRef = useRef<
    Weasley.Xprops.NormalizedAddressForMerchant | EmptyObject
  >({});

  const execute = useCallback(
    async (
      payload: OnShippingChangeCallbackWrapperPayload,
    ): Promise<boolean> => {
      if (typeof onShippingChangeCb !== 'function' || !cartId) {
        logger.info('ON_SHIPPING_ADDRESS_CHANGE_CALLBACK_NOT_PROVIDED');
        return false;
      }

      const {
        formattedShippingAddress: shippingAddress,
        selectedShippingOption,
        isViaShippingOptionsChange,
        setShowLoadingSpinnerWithMessage,
        spinnerMessage,
      } = payload;
      const sanitizedSelectedShippingOption: Weasley.Xprops.NormalizedShippingOption | null =
        selectedShippingOption
          ? normalizeShippingOption(selectedShippingOption)
          : null;

      // This ref keeps track of the address used in the previous
      // run of the callback so that we don't try and run the
      // callback repeatedly for the same address.
      const shippingAddressForMerchant: Weasley.Xprops.NormalizedAddressForMerchant =
        normalizeAddressForMerchant(shippingAddress);

      const hasAddressChanged = !addressAreEqual(
        shippingAddressForMerchant,
        merchantShippingAddressRef.current,
      );

      // The `onShippingChange` can be invoked when the shipping address
      // or shipping options is updated. However, we don't need to apply
      // the address equality check when it's invoked by a shipping option
      // change.
      if (!hasAddressChanged && !isViaShippingOptionsChange) {
        return false;
      }

      const onShippingChangeParams: Weasley.Xprops.OnShippingChangeData = {
        amount: {
          breakdown: buildAmountBreakdown(amounts),
          currency_code: amounts?.total?.currencyCode,
          value: amounts?.total?.currencyValue,
        },
        appName: 'weasley',
        orderID: cartId,
        paymentToken: cartId,
        selected_shipping_option: sanitizedSelectedShippingOption,
        shipping_address: shippingAddressForMerchant,
      };

      // Update our current ref for the shipping address
      // we notified the merchant with.
      merchantShippingAddressRef.current = shippingAddressForMerchant;

      const actions: Weasley.Xprops.OnShippingChangeActions = {
        reject: () => {
          /**
           * Merchants who are using the old `onShippingChange` callback will
           * only reject the address without passing any errors. When merchants
           * call it, it means that the merchant does not support the shipping
           * address provided by the buyer.
           *
           * See {@link https://developer.paypal.com/sdk/js/reference/#link-onshippingchange}
           */
          const error: Error = new Error(
            "Your order can't be shipped to this address.",
          );
          return Promise.reject(error);
        },
        // Based on JS SDK docs, this function indicates to PayPal
        // that you do not need to make any changes to the buyer’s cart.
        // However, none of the other apps are using it including
        // `xoonboardingnodeweb`, so it's a noop function for now.
        resolve: noop,
      };

      try {
        setShowLoadingSpinnerWithMessage(spinnerMessage);

        // ****callback****
        await onShippingChangeCb?.(onShippingChangeParams, actions);
        return true;
      } catch (error: Error | unknown) {
        return Promise.reject(error);
      }
    },
    [cartId, amounts, merchantShippingAddressRef.current],
  );

  return execute;
};
